import { zodResolver } from '@hookform/resolvers/zod'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { DialogContent, InputAdornment, Stack, Tooltip } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import FormDialogActions from '@/components/feedback/FormDialogActions'
import SwitchController from '@/components/inputs/SwitchController'
import TextFieldController from '@/components/inputs/TextFieldController'
import { getAssignResourceToActivationGroupSchema } from '@/features/activationGroup/schemas'
import type { ApiActivationGroupResource, AssignResourceToActivationGroup } from '@/features/activationGroup/types'

type AssignResourceDialogFormProps = {
  activationGroupResource: ApiActivationGroupResource
  isLoading: boolean
  showActivatable: boolean
  onCancel: () => void
  onSubmit: (data: AssignResourceToActivationGroup) => void
}

/**
 * Contains all the logic related to the form to edit a resource from an existing activation group.
 *
 * It is used within the EditResourceDialog component.
 */
const EditResourceDialogForm = ({
  activationGroupResource,
  isLoading,
  showActivatable,
  onCancel,
  onSubmit,
}: AssignResourceDialogFormProps) => {
  const { t } = useTranslation()
  const form = useForm({
    defaultValues: {
      resourceUuid: activationGroupResource.uuid,
      qualifiedCapacity: activationGroupResource.qualifiedCapacity ?? 0,
      activationPriority: activationGroupResource.activationPriority,
      isActivatable: activationGroupResource.isActivatable,
    },
    resolver: zodResolver(getAssignResourceToActivationGroupSchema(t)),
  })

  function handleSubmit(data: AssignResourceToActivationGroup) {
    onSubmit(data)
  }

  return (
    <FormProvider {...form}>
      <form
        noValidate
        aria-label={t('activation_groups.edit_resource_form.title')}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Stack spacing={2}>
            <CustomTypography noWrap fontWeight="bold" variant="body2">
              {t('common.uuid')}
            </CustomTypography>
            <CustomTypography noWrap variant="body1">
              {activationGroupResource.uuid}
            </CustomTypography>
            <CustomTypography noWrap fontWeight="bold" variant="body2">
              {t('common.name')}
            </CustomTypography>
            <CustomTypography noWrap variant="body1">
              {activationGroupResource.name}
            </CustomTypography>
          </Stack>

          <TextFieldController
            required
            label={t('activation_groups.assign_resource_form.qualified_capacity_label')}
            name="qualifiedCapacity"
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">Watts</InputAdornment>,
              },
            }}
            type="number"
          />

          <TextFieldController
            label={t('activation_groups.assign_resource_form.priority_label')}
            name="activationPriority"
            type="number"
          />

          {showActivatable && (
            <Stack alignItems="center" direction="row" gap={0.5}>
              <SwitchController
                label={t('activation_groups.assign_resource_form.is_activatable_label')}
                name="isActivatable"
              />
              <Tooltip title={t('activation_groups.assign_resource_form.is_activatable_help')}>
                <InfoOutlinedIcon color="action" fontSize="small" sx={{ cursor: 'help' }} />
              </Tooltip>
            </Stack>
          )}
        </DialogContent>

        <FormDialogActions isLoading={isLoading} onCancel={onCancel} />
      </form>
    </FormProvider>
  )
}

export default EditResourceDialogForm
